import { Select, Tag } from 'antd';
import type { FC } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { useGetAvailableProviders } from '../../query/districtsTable.hooks';

export interface ProviderSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

export const DTK_PROVIDER_SHORT_NAME = 'DTK';
export const DTK_EXPRESS_PROVIDER_SHORT_NAME = 'DTK_EXPRESS';

export type ProviderState = 'dtk_only' | 'express_only' | 'both' | 'none';

export const getProviderState = (providerShortNames: string[]): ProviderState => {
  const hasDtk = providerShortNames.includes(DTK_PROVIDER_SHORT_NAME);
  const hasExpress = providerShortNames.includes(DTK_EXPRESS_PROVIDER_SHORT_NAME);

  if (hasDtk && hasExpress) return 'both';
  if (hasExpress) return 'express_only';
  if (hasDtk) return 'dtk_only';
  return 'none';
};

export const getProviderStateColor = (state: ProviderState): string => {
  if (state === 'both') return '#1890ff';
  if (state === 'express_only') return '#fa8c16';
  return 'transparent';
};

export const getProviderStateLabel = (state: ProviderState): string => {
  if (state === 'both') return 'DTK + Express';
  if (state === 'express_only') return 'Express Only';
  if (state === 'dtk_only') return 'DTK Only';
  return 'No providers';
};

export const ProviderSelect: FC<ProviderSelectProps> = ({ value, onChange, disabled }) => {
  const { selectedDepartment } = useDepartments();

  const { data: providersData, isLoading } = useGetAvailableProviders({
    countryId: selectedDepartment?.countryId ?? '',
  });

  const providers = providersData?.delivery_provider ?? [];

  return (
    <Select
      mode="multiple"
      style={{ width: '100%' }}
      placeholder="Select delivery providers"
      value={value}
      onChange={onChange}
      loading={isLoading}
      disabled={disabled}
      optionFilterProp="label"
      tagRender={(props) => {
        const { value: providerId, closable, onClose } = props;
        const provider = providers.find((p) => p.id === providerId);
        const isExpress = provider?.short_name === DTK_EXPRESS_PROVIDER_SHORT_NAME;

        return (
          <Tag
            color={isExpress ? 'orange' : 'blue'}
            closable={closable}
            onClose={onClose}
            style={{ marginRight: 3 }}
          >
            {provider?.display_name ?? provider?.short_name ?? providerId}
          </Tag>
        );
      }}
    >
      {providers.map((provider) => (
        <Select.Option key={provider.id} value={provider.id} label={provider.display_name}>
          <Tag color={provider.short_name === DTK_EXPRESS_PROVIDER_SHORT_NAME ? 'orange' : 'blue'}>
            {provider.display_name}
          </Tag>
        </Select.Option>
      ))}
    </Select>
  );
};

export const useDefaultDtkProvider = () => {
  const { selectedDepartment } = useDepartments();

  const { data: providersData } = useGetAvailableProviders({
    countryId: selectedDepartment?.countryId ?? '',
  });

  const dtkProvider = providersData?.delivery_provider?.find(
    (p) => p.short_name === DTK_PROVIDER_SHORT_NAME
  );

  return dtkProvider?.id ?? null;
};
